import React, { useState, forwardRef, useEffect, useRef } from 'react';
import { estimateTokens } from '../lib/api/tokenHelper';

const MAX_TOKENS = 500;

const ChatInput = forwardRef(({ 
  onSendMessage, 
  disabled = false, 
  dailyLimitExceeded = false, 
  onFocus,
  onInputChange,
  placeholder
}, ref) => {
  const [message, setMessage] = useState('');
  const [tokenCount, setTokenCount] = useState(0);
  const [isOverLimit, setIsOverLimit] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const textareaRef = useRef(null);
  const recognitionRef = useRef(null);
  
  // Initialize speech recognition
  useEffect(() => {
    // Check if browser supports speech recognition
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.warn('Speech recognition not supported in this browser');
      return;
    }
    
    // Create speech recognition instance
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    recognition.onstart = () => {
      setIsListening(true);
    };
    
    recognition.onend = () => {
      setIsListening(false);
    };
    
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(result => result[0])
        .map(result => result.transcript)
        .join('');
      
      setMessage(transcript);
      
      // Force focus back to textarea
      if (textareaRef.current) {
        textareaRef.current.focus();
      }
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error', event.error);
      setIsListening(false);
    };
    
    recognitionRef.current = recognition;
    
    // Cleanup
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.stop();
        } catch (err) {
          // Ignore errors when stopping recognition that isn't running
        }
      }
    };
  }, []);
  
  // Toggle speech recognition
  const toggleSpeechRecognition = () => {
    if (!recognitionRef.current) return;
    
    if (isListening) {
      recognitionRef.current.stop();
    } else {
      try {
        recognitionRef.current.start();
      } catch (err) {
        // Handle case where recognition is already started
        console.error('Failed to start speech recognition:', err);
      }
    }
  };

  // Update token count whenever message changes
  useEffect(() => {
    const count = estimateTokens(message);
    setTokenCount(count);
    setIsOverLimit(count > MAX_TOKENS);
    
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
    
    // Notify parent component about input changes
    if (onInputChange) {
      onInputChange(message);
    }
  }, [message, onInputChange]);
  
  // Add method to expose focus functionality
  useEffect(() => {
    // If ref is provided, expose the focus method
    if (ref && typeof ref === 'object') {
      ref.current = {
        ...ref.current,
        focus: () => {
          if (textareaRef.current) {
            textareaRef.current.focus();
          }
        }
      };
    }
  }, [ref]);
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (message.trim() && !disabled && !isOverLimit && !dailyLimitExceeded) {
      onSendMessage(message);
      setMessage('');
      setTokenCount(0);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  // Handle enter key to submit, shift+enter for new line
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };
  
  // Handle focus event
  const handleFocus = (e) => {
    if (onFocus) {
      onFocus(e);
    }
  };
  
  // Determine appropriate placeholder text
  const placeholderText = placeholder || (dailyLimitExceeded ? "Daily limit reached" : "Talk to Pokket");
  
  return (
    <div className="px-4 pb-0 bg-white">
      {dailyLimitExceeded && (
        <div className="mb-2 p-2 bg-yellow-100 text-yellow-800 rounded-md text-sm">
          You've reached your daily token limit (100,000). Please try again tomorrow.
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="relative">
        <textarea
          ref={(node) => {
            // Handle both refs
            textareaRef.current = node;
            if (typeof ref === 'function') {
              ref(node);
            } else if (ref) {
              ref.current = node;
            }
          }}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={placeholderText}
          className={`w-full py-3 px-4 pr-[118px] text-lg font-light rounded-lg border focus:outline-none focus:ring-2 min-h-[56px] max-h-[200px] overflow-hidden ${
            disabled || dailyLimitExceeded ? 'bg-gray-100 cursor-not-allowed border-gray-300' :
            isOverLimit ? 'border-red-300 focus:ring-red-400' : 'border-gray-300 focus:ring-blue-400'
          }`}
          disabled={disabled || dailyLimitExceeded}
          style={{ resize: 'none' }}
          rows="1"
        />
        
        <div className="absolute flex items-center h-[56px] right-0 top-0 pr-3">
          <div className={`text-xs mr-3 ${isOverLimit ? 'text-red-500' : 'text-gray-500'} pl-3`}>
            {tokenCount}/{MAX_TOKENS}
          </div>
          
          <button
            type="button"
            onClick={toggleSpeechRecognition}
            disabled={disabled || dailyLimitExceeded}
            className={`p-2 rounded-full flex items-center justify-center ${
              disabled || dailyLimitExceeded
                ? 'text-gray-400 cursor-not-allowed' 
                : isListening ? 'text-red-500' : 'text-blue-500 hover:bg-blue-50'
            }`}
          >
            <img 
              src="https://db.pokketcoach.com/storage/v1/object/public/pokket-icons//microphone-svgrepo-com.svg"
              alt="Microphone"
              className="w-6 h-6" 
              style={{ filter: isListening ? 'invert(18%) sepia(97%) saturate(6361%) hue-rotate(355deg) brightness(93%) contrast(130%)' : 
                      disabled || dailyLimitExceeded ? 'invert(69%) sepia(0%) saturate(0%) hue-rotate(55deg) brightness(93%) contrast(87%)' : 
                      'invert(38%) sepia(99%) saturate(2254%) hue-rotate(202deg) brightness(95%) contrast(93%)' }}
            />
          </button>
        </div>
      </form>
    </div>
  );
});

export default ChatInput; 